import { actorLabel } from '../../lib/activity-timeline';
import { formatCommentWhen, type CommentDto } from '../../lib/comments';
import { formatTimestampDateTime } from '../../lib/dates';
import { hydrateMentionTokens, type MentionLabel } from '../../lib/rich-text';
import type { MemberDto } from '../../lib/work-types';
import { UserAvatar } from '../UserAvatar';

type Props = {
  comments: CommentDto[];
  members: MemberDto[];
  mentionSuggestions: MentionLabel[];
};

function commentMember(actorId: string | null, members: MemberDto[]): MemberDto | undefined {
  if (!actorId) return undefined;
  return members.find((row) => row.user_id === actorId);
}

export default function CommentThread({ comments, members, mentionSuggestions }: Props) {
  if (comments.length === 0) {
    return <p className="text-sm text-muted-foreground">No comments yet.</p>;
  }

  return (
    <ol className="divide-y divide-border">
      {comments.map((comment) => {
        const name = actorLabel(comment.actor_id, comment.actor_kind, members);
        const member = commentMember(comment.actor_id, members);
        const createdExact = formatTimestampDateTime(comment.created_at) ?? comment.created_at;
        const editedExact = comment.edited_at
          ? formatTimestampDateTime(comment.edited_at) ?? comment.edited_at
          : null;
        return (
          <li key={comment.id} className="flex gap-3 py-4 first:pt-1">
            <span aria-hidden="true">
              <UserAvatar name={name} src={member?.avatar_url} size={28} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-sm font-medium">{name}</span>
                {comment.actor_kind === 'AGENT' ? (
                  <span className="text-xs text-muted-foreground">via agent</span>
                ) : null}
                <time
                  className="text-xs text-muted-foreground"
                  dateTime={comment.created_at}
                  title={createdExact}
                >
                  {formatCommentWhen(comment.created_at)}
                </time>
                {editedExact ? (
                  <span className="text-xs text-muted-foreground" title={editedExact}>
                    Edited
                  </span>
                ) : null}
              </div>
              <div
                className="rich-html mt-1 text-sm"
                dangerouslySetInnerHTML={{
                  __html: hydrateMentionTokens(comment.body, mentionSuggestions),
                }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}
